// 📁 components/Settings/Modules/ModuleGridPage.jsx
//
// Reusable page: grid of modules, toggle per card, min-active enforcement.
//
// Used by:
//   - Products page     — minActive: 1, minActiveKind: 'internal'
//   - Integrations page — minActive: 0
//
// When `minActiveKind` is set to 'internal', only products with kind==='internal'
// count toward the minimum. External products (like Rooms) can be toggled off
// freely. Settings > Products uses this so the org never ends up with only
// external products active (which would leave no SPA dashboard to come back to).

import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Empty, Flex, Typography } from 'zui/Antd';
import { settingsActions } from 'reduxStore/settings/slice';
import { selectCurrentProfileOrgSettings } from 'reduxStore/settings/selectors';
import ModuleCard from '../ModuleCard';

const { Title, Paragraph } = Typography;

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {string} props.description
 * @param {Array}  props.modules
 * @param {number} [props.minActive]        - Minimum total active (default 0)
 * @param {'internal'|null} [props.minActiveKind]
 *        - If set, only modules with `kind === minActiveKind` count toward minActive
 * @param {string} [props.emptyText]
 */
const ModuleGridPage = ({ title, description, modules, minActive = 0, minActiveKind = null, emptyText }) => {
	const dispatch = useDispatch();
	const orgSettings = useSelector(selectCurrentProfileOrgSettings);

	// Resolve enabled state. Convention: Number(value) === 1.
	const moduleStates = useMemo(() => {
		return modules.map((m) => {
			const row = orgSettings.find((s) => s.defaultSettingID === m.moduleFlag);
			const enabled = Number(row?.value) === 1;
			return { ...m, enabled };
		});
	}, [modules, orgSettings]);

	// Count modules that count toward the minimum.
	// If minActiveKind is specified, only count modules of that kind.
	const countingActive = useMemo(() => {
		return moduleStates.filter((m) => {
			if (!m.enabled) return false;
			if (minActiveKind && m.kind !== minActiveKind) return false;
			return true;
		}).length;
	}, [moduleStates, minActiveKind]);

	const handleToggle = (moduleConfig, nextEnabled) => {
		// Guard: don't drop below min for the counted kind.
		// External products can always be toggled off (they don't count).
		const thisCountsTowardMin = !minActiveKind || moduleConfig.kind === minActiveKind;
		if (!nextEnabled && thisCountsTowardMin && countingActive <= minActive) {
			return;
		}

		dispatch(
			settingsActions.upsertOrgSettings({
				settings: [
					{
						defaultSettingID: moduleConfig.moduleFlag,
						value: nextEnabled ? '1' : '0'
					}
				],
				notify: false
			})
		);
	};

	return (
		<Flex vertical gap={24} style={{ width: '100%' }}>
			<div>
				<Title level={3} style={{ margin: 0 }}>
					{title}
				</Title>
				{description && <Paragraph type='secondary'>{description}</Paragraph>}
			</div>

			{moduleStates.length === 0 ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={emptyText || `No ${title.toLowerCase()} configured.`}
				/>
			) : (
				<Flex wrap='wrap' gap={16}>
					{moduleStates.map((m) => {
						// Lock only if this module counts AND turning it off would break the min
						const countsTowardMin = !minActiveKind || m.kind === minActiveKind;
						const isLastCounting = m.enabled && countsTowardMin && countingActive <= minActive;

						return (
							<ModuleCard
								key={m.key}
								logo={m.logo}
								label={m.label}
								description={m.description}
								enabled={m.enabled}
								onToggle={(next) => handleToggle(m, next)}
								disabled={isLastCounting}
								disabledReason={
									isLastCounting
										? `At least ${minActive} ${minActiveKind || ''} product must stay active.`.trim()
										: null
								}
							/>
						);
					})}
				</Flex>
			)}
		</Flex>
	);
};

export default ModuleGridPage;
